import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSocket } from './useSocket';

/**
 * Custom hook for managing enhanced game registry data with i18n support
 */
export const useGameRegistry = () => {
  const { i18n } = useTranslation();
  const { socket, availableGameTypes } = useSocket();

  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Enhanced game data with localization
  const [enhancedGames, setEnhancedGames] = useState([]);

  // Fetch enhanced game data with localization
  const fetchEnhancedGames = useCallback(async (language = null) => {
    if (!socket) return;

    setLoading(true);
    setError(null);

    const fallback = () => availableGameTypes.map(game => ({
      ...game,
      image: `/games/${game.name}/preview.jpg`,
    }));

    try {
      const lang = language || i18n.language || 'en';
      socket.emit('getEnhancedGames', { language: lang });

      let resolved = false;

      const handleGamesResponse = (data) => {
        resolved = true;
        setEnhancedGames(data.games || []);
        setLoading(false);
      };

      const handleGamesError = (errorData) => {
        resolved = true;
        setError(errorData.message || 'Failed to load games');
        setEnhancedGames(fallback());
        setLoading(false);
      };

      socket.on('enhancedGames', handleGamesResponse);
      socket.on('gamesError', handleGamesError);

      const timeout = setTimeout(() => {
        socket.off('enhancedGames', handleGamesResponse);
        socket.off('gamesError', handleGamesError);
        if (!resolved) {
          setEnhancedGames(fallback());
          setLoading(false);
        }
      }, 5000);

      return () => {
        clearTimeout(timeout);
        socket.off('enhancedGames', handleGamesResponse);
        socket.off('gamesError', handleGamesError);
      };
    } catch (err) {
      setError('Failed to fetch enhanced games');
      setEnhancedGames(fallback());
      setLoading(false);
    }
  }, [socket, i18n.language, availableGameTypes]);

  // Basic (guest-accessible) games first, premium games after — stable
  // within each group so server/registry order is otherwise preserved.
  const sortByTier = useCallback((list) => {
    return [...list].sort((a, b) => {
      const aPremium = a.tier === 'premium' ? 1 : 0;
      const bPremium = b.tier === 'premium' ? 1 : 0;
      return aPremium - bPremium;
    });
  }, []);

  // Use enhanced games if available, fallback to basic types only when not loading
  useEffect(() => {
    if (enhancedGames.length > 0) {
      setGames(sortByTier(enhancedGames));
    } else if (availableGameTypes.length > 0 && !loading) {
      // Only use basic fallback when the enhanced fetch has already resolved
      const mappedGames = availableGameTypes.map(game => ({
        ...game,
        image: `/games/${game.name}/preview.jpg`,
        gallery: [],
      }));
      setGames(sortByTier(mappedGames));
    }
  }, [enhancedGames, availableGameTypes, loading, sortByTier]);

  // Load enhanced games when language changes
  useEffect(() => {
    fetchEnhancedGames();
  }, [i18n.language, fetchEnhancedGames]);

  // Game selection management
  const selectGame = useCallback((game) => {
    setSelectedGame(game);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedGame(null);
  }, []);

  // Game filtering and searching
  const getGamesByCategory = useCallback((category) => {
    return games.filter(game => game.category === category);
  }, [games]);

  const getGamesByPlayerCount = useCallback((minPlayers, maxPlayers) => {
    return games.filter(game =>
      game.minPlayers <= maxPlayers && game.maxPlayers >= minPlayers
    );
  }, [games]);

  const searchGames = useCallback((query) => {
    const lowercaseQuery = query.toLowerCase();
    return games.filter(game =>
      game.displayName.toLowerCase().includes(lowercaseQuery) ||
      game.description.toLowerCase().includes(lowercaseQuery) ||
      game.category.toLowerCase().includes(lowercaseQuery)
    );
  }, [games]);

  // Get games that support bots
  const getGamesWithBots = useCallback(() => {
    return games.filter(game => game.supportsBots);
  }, [games]);

  // Get available categories
  const getAvailableCategories = useCallback(() => {
    return [...new Set(games.map(game => game.category))].sort();
  }, [games]);

  // Game validation
  const validateGameSelection = useCallback((game, playerCount) => {
    if (!game) {
      return { isValid: false, reason: 'No game selected' };
    }

    if (playerCount < game.minPlayers) {
      return {
        isValid: false,
        reason: `Minimum ${game.minPlayers} players required`
      };
    }

    if (playerCount > game.maxPlayers) {
      return {
        isValid: false,
        reason: `Maximum ${game.maxPlayers} players allowed`
      };
    }

    return { isValid: true };
  }, []);

  return {
    // Data
    games,
    selectedGame,
    loading,
    error,

    // Actions
    fetchEnhancedGames,
    selectGame,
    clearSelection,

    // Filtering & Search
    getGamesByCategory,
    getGamesByPlayerCount,
    searchGames,
    getGamesWithBots,
    getAvailableCategories,

    // Validation
    validateGameSelection
  };
};

export default useGameRegistry;